import { gql } from '@apollo/client';
import { Box, Divider, Image, Stack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { EventScheduleInfo } from '@/components/EventScheduleInfo';
import { EventStuff } from '@/components/EventStuff';
import { ImagePreview } from '@/components/ImagePreview';
import { TabLink, TabLinkList } from '@/components/TabLink';
import { Card } from '@/imports/ui/chakra/components/Card';
import { ImageFallbackMavericks } from '@/imports/ui/chakra/components/ImageFallbackMavericks';
import { useConferenceContentPropertiesExist } from '@/imports/ui/pages/conference/hooks/useConferenceContentPropertiesExist';

import { HeaderBannerCard_EventFragment } from './graphql-types/HeaderBannerCard_EventFragment';

import { EventControlsMenu } from './components/EventControlsMenu';

export const HeaderBannerCard: React.FC<{
  event: HeaderBannerCard_EventFragment;
}> & { eventFragment: DocumentNode } = ({ event }) => {
  const isEventPast = dayjs().isAfter(event.endDate);

  const { hasSessions, hasBooths, hasDescription, hasVisibleSpeakers, hasAttachments } =
    useConferenceContentPropertiesExist(
      (event.__typename === 'ConferenceEventV2' && event) || undefined
    );

  return (
    <Card>
      <Box position="relative">
        {event.bannerImage?.imageProxy ? (
          <ImagePreview
            src={event.bannerImage.imageProxy}
            alt={event.name}
            w="full"
            display="block"
            borderTopRadius="xl"
            overflow="hidden"
            size="3xl"
            maxH="70vh"
            enableZoom
          >
            <Image
              borderTopRadius="xl"
              src={event.bannerImage.imageProxy}
              alt={event.name}
              w="full"
              fallback={
                <ImageFallbackMavericks
                  borderTopRadius="xl"
                  title={event.name}
                />
              }
            />
          </ImagePreview>
        ) : (
          <ImageFallbackMavericks borderTopRadius="xl" title={event.name} />
        )}
      </Box>

      <Stack p="6" spacing="4">
        <Stack spacing="1">
          <Stack direction="row" alignItems="center">
            <Text textStyle="xlBodyStrong" flex="1">
              {event.name}
            </Text>
            <EventControlsMenu event={event} />
          </Stack>

          <EventScheduleInfo event={event} showAddToCalendar={!isEventPast} />
        </Stack>

        <EventStuff
          event={event}
          allowSpecialTooltip={true}
          allowConferenceJoin={false}
        />
      </Stack>

      {event.__typename === 'ConferenceEventV2' && (
        <>
          <Divider />

          <TabLinkList px="6">
            {(hasDescription || hasVisibleSpeakers) && (
              <TabLink href={`/events/conferences/${event._id}/overview`}>
                Overview
              </TabLink>
            )}

            {hasSessions && (
              <TabLink href={`/events/conferences/${event._id}/sessions`}>
                Sessions
              </TabLink>
            )}
            
            {hasAttachments &&(<TabLink href={`/events/conferences/${event._id}/attachments`}>
              Attachments
            </TabLink>
            )}

            {hasBooths && (
              <TabLink href={`/events/conferences/${event._id}/expo`}>
                Booths
              </TabLink>
            )}
          </TabLinkList>
        </>
      )}
    </Card>
  );
};

HeaderBannerCard.eventFragment = gql`
  fragment HeaderBannerCard_EventFragment on EventV2 {
    _id
    name
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
    startDate
    endDate
    ... on SimpleEventV2 {
      _id
    }
    ... on ConferenceEventV2 {
      _id
      ...useConferenceContentPropertiesExist_ConferenceEventFragment
    }
    ...EventControlsMenu_EventFragment
    ...EventStuff_EventFragment
    ...EventScheduleInfo_EventFragment
  }
  ${useConferenceContentPropertiesExist.conferenceEventFragment}
  ${EventControlsMenu.eventFragment}
  ${EventScheduleInfo.eventFragment}
  ${EventStuff.eventFragment}
`;
